import { useEffect } from 'react';
import { getMedia } from '../persistence/v2Database';
import { createStore, useStore } from './createStore';

// Object-URL cache for photo bytes stored in the V2 media store. Loading is lazy and
// fail-safe: a missing blob or a host without URL.createObjectURL (jsdom) simply yields
// null and the UI shows a placeholder.
type Variant = 'full' | 'thumbnail';

interface MediaUrlState {
  urls: Record<string, string>;
}

export const mediaUrlStore = createStore<MediaUrlState>({ urls: {} });

const inflight = new Set<string>();
const misses = new Set<string>();

function keyOf(evidenceId: string, variant: Variant): string {
  return `${variant}:${evidenceId}`;
}

function canCreateUrls(): boolean {
  return typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';
}

export function requestMediaUrl(evidenceId: string, variant: Variant): void {
  // An empty key means "nothing to show" (no cover photo, no inspector photo): never a lookup.
  if (evidenceId.length === 0) return;
  const key = keyOf(evidenceId, variant);
  if (mediaUrlStore.get().urls[key] || inflight.has(key) || misses.has(key) || !canCreateUrls()) return;
  inflight.add(key);
  void (async () => {
    try {
      let blob = await getMedia(evidenceId, variant);
      if (!blob && variant === 'thumbnail') blob = await getMedia(evidenceId, 'full');
      if (!blob) {
        misses.add(key);
        return;
      }
      const url = URL.createObjectURL(blob);
      mediaUrlStore.set((prev) => ({ urls: { ...prev.urls, [key]: url } }));
    } catch {
      misses.add(key);
    } finally {
      inflight.delete(key);
    }
  })();
}

export function releaseMediaUrls(evidenceIds: readonly string[]): void {
  const keys = evidenceIds.flatMap((id) => [keyOf(id, 'full'), keyOf(id, 'thumbnail')]);
  const current = mediaUrlStore.get().urls;
  const next = { ...current };
  let changed = false;
  for (const key of keys) {
    misses.delete(key);
    const url = current[key];
    if (!url) continue;
    changed = true;
    delete next[key];
    try {
      URL.revokeObjectURL(url);
    } catch {
      // nothing to release
    }
  }
  if (changed) mediaUrlStore.set(() => ({ urls: next }));
}

// Account isolation (2026-09-21): media keys repeat across accounts (the inspector avatar has ONE
// fixed key per database), so nothing cached for one account may be shown to the next.
export function releaseAllMediaUrls(): void {
  const current = mediaUrlStore.get().urls;
  misses.clear();
  inflight.clear();
  for (const url of Object.values(current)) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // nothing to release
    }
  }
  if (Object.keys(current).length > 0) mediaUrlStore.set(() => ({ urls: {} }));
}

export function useMediaUrl(evidenceId: string, variant: Variant): string | null {
  const key = keyOf(evidenceId, variant);
  const url = useStore(mediaUrlStore, (s) => s.urls[key] ?? null);
  useEffect(() => {
    requestMediaUrl(evidenceId, variant);
  }, [evidenceId, variant]);
  return url;
}
